import {i18n} from '@online-offer/i18n';
import {type MoneyResponseBody} from '@online-offer/shared';
import {Box, Display, HStack, Show, Text, VStack} from 'platform/foundation';
import {formatNumber, useFormatCurrency, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {FinancialProductResultRow} from './FinancialProductResultRow';
import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatFinancialProductPercentage} from './formatFinancialProductPercentage';
import {getLeasingTargetGroupLabelKey} from './getLeasingTargetGroupLabelKey';

interface FinancialProductOfferDetailProps extends TestIdProps {
  offer: FinancialProductOffer;
}

/**
 * Amounts are rendered from the backend quote without recalculation; rate brackets stay hidden.
 */
export function FinancialProductOfferDetail(props: FinancialProductOfferDetailProps) {
  const formatCurrency = useFormatCurrency();
  const locale = useLocale();
  const calculation = props.offer.calculation;
  const selection = props.offer.selection;

  const formatMoney = (money: MoneyResponseBody | Nullish) =>
    formatFinancialProductMoney(money, formatCurrency);

  const formatPercentageWithAmount = (
    percentage: number | Nullish,
    amount: MoneyResponseBody | Nullish
  ) =>
    isNil(percentage)
      ? formatMoney(amount)
      : [formatFinancialProductPercentage(percentage), formatMoney(amount)]
          .filter(isNotNil)
          .join(' · ');

  const metadata = isNil(selection)
    ? null
    : match(props.offer.productType)
        .with('LEASING', () =>
          i18n.t('financialProducts.resultLeasing', {term: selection.termMonths})
        )
        .otherwise(() =>
          isNil(selection.downPaymentPercentage)
            ? i18n.t('financialProducts.monthsValue', {value: selection.termMonths})
            : i18n.t('financialProducts.result', {
                term: selection.termMonths,
                downPayment: selection.downPaymentPercentage,
              })
        );

  const figures = match(props.offer.productType)
    .with('LEASING', () => [
      {
        label: i18n.t('financialProducts.customerType'),
        value: isNil(selection?.leasingTargetGroup)
          ? null
          : i18n.t(getLeasingTargetGroupLabelKey(selection.leasingTargetGroup)),
      },
      {
        label: i18n.t('financialProducts.annualMileage'),
        value: isNil(selection?.annualMileage)
          ? null
          : i18n.t('financialProducts.kilometresPerYearValue', {
              value: formatNumber(locale.localeConfig, selection.annualMileage, 0),
            }),
      },
      {
        label: i18n.t('financialProducts.downPayment'),
        value: formatMoney(calculation?.leasingDownPayment),
      },
    ])
    .otherwise(() => [
      {
        label: i18n.t('financialProducts.nominalRate'),
        value: isNil(calculation?.nominalRate)
          ? null
          : formatFinancialProductPercentage(calculation.nominalRate),
      },
      {
        label: i18n.t('financialProducts.apr'),
        value: isNil(calculation?.apr) ? null : formatFinancialProductPercentage(calculation.apr),
      },
      {
        label: i18n.t('financialProducts.downPayment'),
        value: formatPercentageWithAmount(
          selection?.downPaymentPercentage,
          calculation?.downPaymentAmount
        ),
      },
      {
        label: i18n.t('financialProducts.netCredit'),
        value: formatMoney(calculation?.netCredit),
      },
      {
        label: i18n.t('financialProducts.grossCredit'),
        value: formatMoney(calculation?.grossCredit),
      },
      {
        label: i18n.t('financialProducts.balloonPayment'),
        value: formatPercentageWithAmount(
          selection?.balloonPaymentPercentage,
          calculation?.balloonPaymentAmount
        ),
      },
      {
        label: i18n.t('financialProducts.totalInterest'),
        value: formatMoney(calculation?.totalInterest),
      },
      {
        label: i18n.t('financialProducts.totalPayable'),
        value: formatMoney(calculation?.totalPayable),
        emphasized: true,
      },
    ]);

  return (
    <VStack spacing={5} data-testid={suffixTestId('offer', props)}>
      <VStack spacing={1}>
        <Show when={isNotNil(metadata)}>
          <Box opacity={0.62}>
            <Text size="small" color="inverted">
              {metadata}
            </Text>
          </Box>
        </Show>
        <Show when={isNotNil(calculation)}>
          <HStack spacing={2} align="baseline" wrap>
            <div data-testid={`${suffixTestId('monthlyPayment', props)}-heading`}>
              <Display size={1} headingLevel={2} color="inverted">
                {formatMoney(calculation?.monthlyPayment)}
              </Display>
            </div>
            <Box opacity={0.74}>
              <Text size="large" color="inverted">
                {i18n.t('financialProducts.perMonth')}
              </Text>
            </Box>
          </HStack>
        </Show>
      </VStack>
      <VStack spacing={0}>
        {figures.map((figure) => (
          <FinancialProductResultRow key={figure.label} {...figure} />
        ))}
      </VStack>
    </VStack>
  );
}
